import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Power, Code2 } from "lucide-react";
import { api } from "../lib/api";

const PLACEMENTS = [
  { id: "head", label: "<head>" },
  { id: "body-start", label: "Start of <body>" },
  { id: "body-end", label: "End of <body>" },
];

const EMPTY = { name: "", placement: "head", code: "", enabled: true };

export default function TrackingAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/tracking");
      setItems(data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/admin/tracking", draft);
      setItems((x) => [data, ...x]);
      setDraft(EMPTY);
      toast.success(`Added ${data.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  const patch = async (id, changes) => {
    try {
      const { data } = await api.patch(`/admin/tracking/${id}`, changes);
      setItems((x) => x.map((s) => s.id === id ? data : s));
      toast.success("Saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    try {
      await api.delete(`/admin/tracking/${s.id}`);
      setItems((x) => x.filter((y) => y.id !== s.id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  if (loading) return <p className="text-neutral-400 text-lg">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="tracking-admin">
      <form onSubmit={create} className="bg-[#141414] border border-white/10 rounded-2xl p-5 md:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Code2 size={18} className="text-[#ffd100]" />
          <h3 className="font-display text-xl text-white">Add tracking snippet</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <input data-testid="tracking-name" required placeholder="Name (e.g. Google Analytics, Meta Pixel)"
            value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            className="bg-[#0a0a0a] border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#ffd100] outline-none" />
          <select data-testid="tracking-placement" value={draft.placement} onChange={(e) => setDraft({ ...draft, placement: e.target.value })}
            className="bg-[#0a0a0a] border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#ffd100] outline-none">
            {PLACEMENTS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </div>
        <textarea data-testid="tracking-code" required rows={6}
          placeholder="Paste your full snippet (e.g. Google Analytics gtag) — script tags included"
          value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })}
          className="w-full bg-[#0a0a0a] border border-white/20 rounded-lg px-4 py-3 text-white font-mono text-sm focus:border-[#ffd100] outline-none resize-y" />
        <button type="submit" disabled={saving} data-testid="create-tracking-btn"
          className="mt-4 inline-flex items-center gap-2 bg-[#ffd100] text-[#0a0a0a] font-bold px-5 py-2.5 rounded-full hover:bg-[#e5bc00] transition-colors disabled:opacity-60">
          <Plus size={16} /> {saving ? "Adding…" : "Add snippet"}
        </button>
      </form>

      <div className="space-y-3">
        {items.length === 0 && (
          <p className="text-neutral-500 text-center py-10 text-lg">
            No snippets yet — add your first tracking code above.
          </p>
        )}
        {items.map((s) => <SnippetRow key={s.id} s={s} onPatch={patch} onDelete={del} />)}
      </div>
    </div>
  );
}

function SnippetRow({ s, onPatch, onDelete }) {
  const [code, setCode] = useState(s.code);
  const [placement, setPlacement] = useState(s.placement);
  const [name, setName] = useState(s.name);
  const dirty = code !== s.code || placement !== s.placement || name !== s.name;

  return (
    <div data-testid={`snippet-${s.id}`} className={`bg-[#141414] border rounded-xl p-5 ${s.enabled ? "border-[#ffd100]/30" : "border-white/10 opacity-70"}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex-1 min-w-[200px]">
          <input value={name} onChange={(e) => setName(e.target.value)}
            className="w-full bg-transparent text-white font-display text-lg outline-none border-b border-transparent focus:border-white/20" />
          <div className="flex items-center gap-2 mt-1">
            <select value={placement} onChange={(e) => setPlacement(e.target.value)}
              className="bg-[#0a0a0a] border border-white/15 rounded-md px-2 py-0.5 text-xs text-neutral-300">
              {PLACEMENTS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
            <span className={`text-xs font-bold ${s.enabled ? "text-emerald-400" : "text-neutral-500"}`}>
              {s.enabled ? "● LIVE" : "○ Paused"}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => onPatch(s.id, { enabled: !s.enabled })}
            data-testid={`toggle-${s.id}`}
            className="inline-flex items-center gap-1.5 border-2 border-white/20 text-white font-semibold px-3 py-1.5 rounded-full hover:border-[#ffd100] hover:text-[#ffd100] transition-colors text-sm">
            <Power size={13} /> {s.enabled ? "Pause" : "Enable"}
          </button>
          <button onClick={() => onDelete(s)}
            data-testid={`delete-${s.id}`}
            className="inline-flex items-center gap-1.5 border-2 border-red-500/40 text-red-300 font-semibold px-3 py-1.5 rounded-full hover:bg-red-500 hover:text-white hover:border-red-500 transition-colors text-sm">
            <Trash2 size={13} /> Delete
          </button>
        </div>
      </div>
      <textarea rows={5} value={code} onChange={(e) => setCode(e.target.value)}
        className="w-full bg-[#0a0a0a] border border-white/20 rounded-lg px-4 py-3 text-white font-mono text-sm focus:border-[#ffd100] outline-none resize-y" />
      {dirty && (
        <button onClick={() => onPatch(s.id, { name, placement, code })}
          data-testid={`save-${s.id}`}
          className="mt-3 inline-flex items-center gap-2 bg-[#ffd100] text-[#0a0a0a] font-bold px-4 py-2 rounded-full hover:bg-[#e5bc00] transition-colors text-sm">
          <Save size={14} /> Save changes
        </button>
      )}
    </div>
  );
}
